import { useEffect, useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { ClaimLimitsConfig, DanceConfig, RecruitConfig } from './types';

// Mặc định của nhiệm vụ mở rộng — cùng số với STAR_DEFAULT_CONFIG phía backend
// (docs/nghien-cuu-diem-sao/SPEC-NHIEM-VU-MO-RONG.md §1). Trang Cấu hình dùng lại.
export const RECRUIT_DEFAULTS: RecruitConfig = {
  registerWindowDays: 30,
  liveDayMinMinutes: 60,
  qualifyWindowDays: 60,
  qualifyLiveDays: 20,
  qualifyLiveHours: 40,
  qualifyStar: 40,
  retainAtDays: 90,
  retainLiveDays: 12,
  retainStar: 30,
  standoutWindowDays: 90,
  standoutKcDays: 36,
  standoutStar: 40,
  manualGraceDays: 14,
  dataLagDays: 3,
  monthlyCap: 150,
};

export const DANCE_DEFAULTS: DanceConfig = {
  practiceTiers: [
    { score: 5, star: 3 },
    { score: 8, star: 5 },
  ],
  practiceMaxPerWeek: 4,
  examTiers: [
    { score: 8, star: 15 },
    { score: 9, star: 20 },
  ],
  examMaxPerMonth: 2,
  backdateDays: 2,
  editHours: 24,
};

// Giới hạn chống spam nhiệm vụ nộp tay — cùng số với STAR_DEFAULT_CONFIG.claimLimits phía backend
// (docs/nghien-cuu-diem-sao/10-can-doi-kpi-va-gioi-han-nhiem-vu.md §5.3). 0 = không giới hạn.
export const CLAIM_LIMITS_DEFAULTS: ClaimLimitsConfig = {
  submissionsPerDayByGroup: { STAFF: 3, CREATOR: 5, NPC: 5 },
  maxPending: 5,
  resubmitMax: 2,
  monthlyCapByGroup: { STAFF: 100, CREATOR: 150, NPC: 150 },
};

interface StarRules {
  recruit: RecruitConfig;
  dance: DanceConfig;
  /** false = đang dùng số mặc định (MOD không đọc được Cấu hình, hoặc chưa tải xong). */
  fromServer: boolean;
}

/**
 * Luật tuyển dụng / chấm nhảy đang áp dụng, để in chữ hướng dẫn trên trang. GET /stars/admin/config
 * chỉ ADMIN gọi được → MOD thấy số mặc định (kèm chữ "mặc định"). Lỗi thì im lặng giữ mặc định.
 */
export function useStarRules(): StarRules {
  const { loaded, isAdmin } = useCurrentRoles();
  const [rules, setRules] = useState<StarRules>({
    recruit: RECRUIT_DEFAULTS,
    dance: DANCE_DEFAULTS,
    fromServer: false,
  });

  useEffect(() => {
    if (!loaded || !isAdmin) return undefined;
    let cancelled = false;
    AxiosUtils.get(API.starsConfig)
      .then((res: any) => {
        const data = res?.data;
        if (cancelled || !data || typeof data !== 'object') return;
        setRules({
          recruit: { ...RECRUIT_DEFAULTS, ...(data.recruit || {}) },
          dance: { ...DANCE_DEFAULTS, ...(data.dance || {}) },
          fromServer: true,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [loaded, isAdmin]);

  return rules;
}
